import type { GridSortModel } from '@mui/x-data-grid-premium';

export type MultiSortDirection = 'asc' | 'desc';

export type MultiSortRule = {
  field: string;
  sort: MultiSortDirection;
};

export type MultiSortColumnDefinition = {
  field: string;
  headerName?: string;
  sortable?: boolean;
  type?: string;
  sortingOrder?: readonly (MultiSortDirection | null | undefined)[];
  hidden?: boolean;
};

export type MultiSortColumnOption = {
  field: string;
  label: string;
  addable: boolean;
  directions: readonly MultiSortDirection[];
  hidden: boolean;
};

type MultiSortRuleUpdate = Partial<MultiSortRule>;

const isMultiSortDirection = (value: unknown): value is MultiSortDirection => value === 'asc' || value === 'desc';

export const buildMultiSortColumnOptions = (
  columns: readonly MultiSortColumnDefinition[],
  activeModel: GridSortModel,
  defaultSortingOrder: readonly (MultiSortDirection | null | undefined)[] = ['asc', 'desc', null]
): MultiSortColumnOption[] => {
  const activeDirectionByField = new Map(
    activeModel
      .filter((item): item is MultiSortRule => isMultiSortDirection(item.sort))
      .map(({ field, sort }) => [field, sort])
  );
  const optionByField = new Map<string, MultiSortColumnOption>();

  columns.forEach((column) => {
    const activeDirection = activeDirectionByField.get(column.field);
    const configuredDirections = (column.sortingOrder ?? defaultSortingOrder).filter(isMultiSortDirection);
    const directions = Array.from(new Set(configuredDirections));

    if (activeDirection && !directions.includes(activeDirection)) {
      directions.unshift(activeDirection);
    }

    const isActive = activeDirection != null;
    const canBeAdded = column.sortable !== false && column.type !== 'actions' && directions.length > 0;

    if (!isActive && !canBeAdded) return;

    optionByField.set(column.field, {
      field: column.field,
      label: column.headerName?.trim() || column.field,
      addable: canBeAdded,
      directions,
      hidden: column.hidden ?? false,
    });
  });

  const options = Array.from(optionByField.values());
  const labelOccurrences = options.reduce<Map<string, number>>((occurrences, { label }) => {
    occurrences.set(label, (occurrences.get(label) ?? 0) + 1);
    return occurrences;
  }, new Map());

  return options.map((option) => ({
    ...option,
    label: labelOccurrences.get(option.label) === 1 ? option.label : `${option.label} (${option.field})`,
  }));
};

export const sanitizeMultiSortModel = (
  model: GridSortModel,
  columns: readonly MultiSortColumnOption[]
): MultiSortRule[] => {
  const allowedFields = new Set(columns.map(({ field }) => field));
  const includedFields = new Set<string>();

  return model.reduce<MultiSortRule[]>((rules, item) => {
    const option = columns.find(({ field }) => field === item.field);

    if (
      !allowedFields.has(item.field) ||
      includedFields.has(item.field) ||
      !isMultiSortDirection(item.sort) ||
      !option?.directions.includes(item.sort)
    ) {
      return rules;
    }

    includedFields.add(item.field);
    rules.push({ field: item.field, sort: item.sort });
    return rules;
  }, []);
};

export const addMultiSortRule = (
  model: readonly MultiSortRule[],
  columns: readonly MultiSortColumnOption[]
): MultiSortRule[] => {
  const selectedFields = new Set(model.map(({ field }) => field));
  const nextColumn = columns.find(({ field, addable }) => addable && !selectedFields.has(field));
  const defaultDirection = nextColumn?.directions[0];

  return nextColumn && defaultDirection ? [...model, { field: nextColumn.field, sort: defaultDirection }] : [...model];
};

export const updateMultiSortRule = <T extends MultiSortRule>(
  model: readonly T[],
  index: number,
  update: MultiSortRuleUpdate,
  columns: readonly MultiSortColumnOption[]
): T[] => {
  if (index < 0 || index >= model.length) return [...model];

  const currentRule = model[index];
  const nextField = update.field ?? currentRule.field;
  const nextColumn = columns.find(({ field }) => field === nextField);
  const requestedSort = update.sort ?? currentRule.sort;
  const nextSort = nextColumn?.directions.includes(requestedSort) ? requestedSort : nextColumn?.directions[0];
  const isAllowedField = nextColumn != null && (nextField === currentRule.field || nextColumn.addable);
  const isDuplicateField = model.some(({ field }, ruleIndex) => ruleIndex !== index && field === nextField);

  if (!isAllowedField || isDuplicateField || !nextSort) return [...model];

  return model.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, field: nextField, sort: nextSort } : rule));
};

export const removeMultiSortRule = <T extends MultiSortRule>(model: readonly T[], index: number): T[] =>
  index < 0 || index >= model.length ? [...model] : model.filter((_, ruleIndex) => ruleIndex !== index);

export const moveMultiSortRule = <T extends MultiSortRule>(
  model: readonly T[],
  index: number,
  direction: -1 | 1
): T[] => {
  const targetIndex = index + direction;

  if (index < 0 || index >= model.length || targetIndex < 0 || targetIndex >= model.length) {
    return [...model];
  }

  const reorderedModel = [...model];
  [reorderedModel[index], reorderedModel[targetIndex]] = [reorderedModel[targetIndex], reorderedModel[index]];
  return reorderedModel;
};

export const areMultiSortModelsEqual = (first: readonly MultiSortRule[], second: readonly MultiSortRule[]): boolean =>
  first.length === second.length &&
  first.every((rule, index) => rule.field === second[index].field && rule.sort === second[index].sort);
